import moment from 'moment';
import {
  Text,
  Mail,
  Image,
  Paperclip,
  AppWindow,
  List,
  Globe,
  StickyNote,
  MessageSquare,
  Link,
  Phone,
  Hash,
  Calendar,
  Car,
  Landmark,
  Regex,
  CircleHelp,
  type LucideIcon,
} from '@lucide/vue';

export const statusTypes = {
  online: 'online',
  offline: 'offline',
  otherTab: 'otherTab',
} as const;

export const incomeTypes = {
  newChat: 'newChat',
  newMessage: 'newMessage',
  activeTab: 'activeTab',
  none: null,
} as const;

export const agentForOrganizationStatusTypes = {
  isAgentForCurrentOrganization: 'isAgentForCurrentOrganization',
  isAgentForOtherOrganization: 'isAgentForOtherOrganization',
  isNotAgent: 'isNotAgent',
} as const;

export const chatStatus = {
  unassigned: 'unassigned',
  open: 'open',
  closed: 'closed',
  offline: 'offline',
} as const;

export const infoTagTypes = {
  dialogs: 'dialogs',
  website: 'website',
  channel: 'channel',
  language: 'language',
  link: 'link',
  attachment: 'attachment',
  note: 'note',
  form: 'form',
} as const;

export const infoCardTypes = {
  text: 'text',
  email: 'email',
  attachment: 'attachment',
  image: 'image',
  phone: 'phone',
  link: 'link',
  number: 'number',
  date: 'date',
  licenseplate: 'licensePlate',
  iban: 'iban',
  regexp: 'regexp',
} as const;

/** A raw livechat/bot message — either a live socket message or a history entry. Backend payload, intentionally loose. */
export interface RawLivechatMessage {
  /** Nested history-message body (`message.message.text` holds the actual text for history entries). */
  message?: { message?: { text?: string } };
  /** Plain text (alternative history shape). */
  text?: string;
  /** Message payload used by live (non-history) messages. */
  data?: any;
  /** Present when the message came from the end user. */
  user?: any;
  /** Truthy when the message was sent by a livechat agent. */
  livechat?: any;
  /** Message type discriminator (e.g. text/attachment). */
  type?: string;
  /** Timestamp of history messages. */
  date?: string | Date;
  /** Timestamp of live messages. */
  created_at?: string | Date;
  /** Unique message id. */
  uuid?: string;
  [key: string]: any;
}

/** Normalized message shape produced by {@link toProperMsgFormat} for the chat UI. */
export interface ProperMessage {
  /** The display text/payload of the message. */
  data: any;
  /** Who sent it — user (with profile fields), agent, or bot. */
  sender: Record<string, any>;
  /** Message type discriminator carried over from the raw message. */
  type?: string;
  /** Timestamp (history `date` wins over `created_at`). */
  created_at?: string | Date;
  /** Whether the message was authored by a livechat agent. */
  livechat: any;
  /** Unique message id. */
  uuid?: string;
  /** True when the text came from the history shape rather than a live payload. */
  isHistory?: boolean;
}

/** Extracts the display text from any of the three raw message shapes (history-nested, plain text, live data). */
export const extractMsgText = (m: RawLivechatMessage): any => {
  if (m.message?.message?.text) {
    return m.message.message.text;
  } else if (m.text) {
    return m.text;
  } else {
    return m.data || "";
  }
}

/**
 * Normalizes a history message into the message shape the chat UI renders.
 * @param msg a history message object
 * @param activeChat the currently open chat — its `user` is merged into the sender for user messages
 * @returns a message object
 */
export const toProperMsgFormat = (msg: RawLivechatMessage, activeChat: { user?: Record<string, any> }): ProperMessage => {
  let sender: Record<string, any> = {};

  if (msg.user) {
    sender = {
      ...activeChat.user,
      role: 'user',
    };
  } else if (msg.livechat) {
    sender = {
      role: 'agent',
    };
  } else {
    sender = {
      role: 'bot',
    };
  }

  const message: ProperMessage = {
    data: '',
    sender,
    type: msg.type,
    created_at: msg.date  || msg.created_at,
    livechat: msg.livechat || false,
    uuid: msg.uuid,
  };

  if (msg.message?.message?.text) {
    message.data = msg.message?.message?.text;
    message.isHistory = true
  } else if (msg.text) {
    message.data = msg.text;
    message.isHistory = true
  } else {
    message.data = msg.data;
    message.isHistory = false;
  }

  return message;
}

/**
 * Icon component for info cards / info tags.
 *
 * The Vue 2 dashboard rendered info-card/info-tag icons as Material Design Icon
 * font strings (e.g. `mdi-email`) via Vuetify's `<v-icon>` (a legacy
 * `infoCardIcon` string map that has since been removed together with
 * @mdi/font). The Vue 3 dashboard uses Lucide components, so components import
 * this map instead and render the returned component with `<component :is="...">`.
 *
 * @param type one of {@link infoCardTypes} / {@link infoTagTypes}
 * @returns a Lucide-vue-next icon component
 */
export const infoCardLucideIcon = (type: string): LucideIcon => {
  switch (type) {
    case infoCardTypes.text:
      return Text;
    case infoCardTypes.email:
      return Mail;
    case infoCardTypes.image:
      return Image;
    case infoTagTypes.attachment:
    case infoCardTypes.attachment:
      return Paperclip;
    case infoTagTypes.website:
      return AppWindow;
    case infoTagTypes.channel:
      return List;
    case infoTagTypes.language:
      return Globe;
    case infoTagTypes.note:
      return StickyNote;
    case infoTagTypes.form:
    case infoTagTypes.dialogs:
      return MessageSquare;
    case infoTagTypes.link:
    case infoCardTypes.link:
      return Link;
    case infoCardTypes.phone:
      return Phone;
    case infoCardTypes.number:
      return Hash;
    case infoCardTypes.date:
      return Calendar;
    case infoCardTypes.licenseplate:
      return Car;
    case infoCardTypes.iban:
      return Landmark;
    case infoCardTypes.regexp:
      return Regex;
    default:
      return CircleHelp;
  }
};

/**
 * Normalizes a language code, because sometimes we get AINA codes (`en-US`) and other times ISO
 * (`en`). Our Hub uses the ISO ones for countries and language names.
 * @param code raw language code; anything that is not a string is passed through untouched
 * @returns first part of the language code, forced to lowercase
 */
export const normalizeLanguageCode = (code: any): any => {
  if (typeof code !== 'string') return code;
  const [normalized] = code.split(/[-_]|[^\w]/g);
  return (normalized ?? code).toLowerCase();
};

/** Sort comparator: ascending by `updated_at` timestamp. */
export const sortUpdated = (a: { updated_at?: string | Date }, b: { updated_at?: string | Date }): number => {
  const aDate = moment(a.updated_at);
  const bDate = moment(b.updated_at);
  return moment(aDate).diff(bDate);
};

/**
 * Sort comparator that pushes offline agents behind others.
 * NOTE: intentionally returns undefined when `b` is not offline (pre-existing behavior).
 */
export const sortOffline = (a: { status?: string }, b: { status?: string }): number | undefined => {
  if (b.status === 'offline') {
    return -1;
  }
};

/**
 * Human-readable German "time ago" label for a chat's last activity.
 * @param lastDate the timestamp to describe
 * @param now reference "current" time; defaults to now
 * @param extended when true, dates older than 24h render as absolute dates instead of "vor mehr als 24h"
 * @returns label string, or undefined for the (unreachable) fall-through case
 */
export function getTimeString(lastDate: string | Date, now: Date = new Date(), extended?: boolean): string | undefined {
  const diffMinutes = moment(now).diff(moment(lastDate), 'minutes');
  const currentYear = moment(now).year() === moment(lastDate).year()

  if (diffMinutes < 1) return 'gerade eben';
  if (diffMinutes >= 1 && diffMinutes <= 60) return `vor ${diffMinutes} Min.`;

  if (diffMinutes > 60) {
    const diffHours = moment(now).diff(moment(lastDate), 'hours');
    if (diffHours <= 24) return `vor mehr als ${diffHours}h`;

    if (diffHours > 24 && !extended) return `vor mehr als 24h`;

    if (currentYear) return moment(lastDate).format('YYYY-MM-DD HH:mm');
    return moment(lastDate).format('YYYY-MM-DD');
  }
}

/** A browser tab registered by the multi-tab livechat coordination (stored in localStorage). */
interface LivechatTab {
  /** Unique id of the browser tab (session-scoped). */
  id: string;
  /** Whether this tab is the one currently designated to receive chats. */
  isActive?: boolean;
}

/**
 * Multi-tab guard: true when there is no active tab registered, or the current
 * session's tab IS the active one (i.e. this tab may act on livechat events).
 */
export function checkTabIsOk({ tabsLocal, tabIdSession }: { tabsLocal?: LivechatTab[] | null; tabIdSession?: string }): boolean {
  const activeTab = tabsLocal?.find(tab => tab.isActive);
  //returns true if there is no active tab or the current tab is the active one
  return !activeTab || activeTab.id === tabIdSession;
}
